/** Shared pieces of the compound grammars and of the archetype asserts. */

import { collidersFor } from '../../sim/props';
import { OBJECTIVES } from '../../sim/tuning';
import type { EnemyKind, MissionDef, ObjectiveRule, PropDef, PropKind, Rect, SpawnDef } from '../../sim/types';
import { NO_BUILD, NO_STAND } from '../builder';
import { FLAG } from '../grid';
import { overlooksQuietApproach } from '../quietSide';
import type { Point } from '../types';
import type { GoalCtx } from './types';

export interface StructureOptions {
  target?: boolean;
  /** Keep at least this far (centre to centre) from every point in `from`; the new prop is added to it. */
  minDist?: number;
  from?: Point[];
  /** Keep within this distance of a point (a guard house "adjacent" to the prison). */
  near?: Point;
  maxDist?: number;
  within?: Rect;
  margin?: number;
  yaw?: number;
  length?: number;
  /** Roads may run through a compound; most buildings stay off them, clutter need not. */
  onRoad?: boolean;
}

const snap = (v: number): number => Math.round(v * 2) / 2;

/** Place a structure somewhere in the compound that satisfies the spacing constraints, or return null. */
export function structure(g: GoalCtx, kind: PropKind, o: StructureOptions = {}): PropDef | null {
  const rect = o.within ?? g.inner;
  const shape = o.length !== undefined ? { length: o.length } : {};
  const b = g.map.bounds(kind, 0, 0, shape);
  const hx = Math.max(-b.x0, b.x1);
  const hz = Math.max(-b.z0, b.z1);
  if (rect.x1 - rect.x0 < hx * 2 || rect.z1 - rect.z0 < hz * 2) return null;
  const avoid = o.onRoad === true ? NO_BUILD & ~FLAG.road : NO_BUILD;
  for (let tries = 0; tries < 120; tries++) {
    const x = snap(g.rng.range(rect.x0 + hx, rect.x1 - hx));
    const z = snap(g.rng.range(rect.z0 + hz, rect.z1 - hz));
    const d2 = (p: Point): number => (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z);
    if (o.from && o.minDist !== undefined && o.from.some((p) => d2(p) < (o.minDist ?? 0) * (o.minDist ?? 0))) continue;
    if (o.near && o.maxDist !== undefined && d2(o.near) > o.maxDist * o.maxDist) continue;
    const yaw = o.yaw !== undefined ? { yaw: o.yaw } : {};
    const target = o.target === true ? { target: true } : {};
    const prop = g.map.place(kind, x, z, { margin: o.margin ?? 1.5, avoid, ...shape, ...yaw, ...target });
    if (!prop) continue;
    o.from?.push({ x: prop.x, z: prop.z });
    return prop;
  }
  return null;
}

/** As `structure`, but the mission cannot exist without it: failing marks the attempt for a re-roll. */
export function required(g: GoalCtx, kind: PropKind, o: StructureOptions = {}): PropDef {
  const prop = structure(g, kind, o);
  if (prop) return prop;
  g.map.problems.push(`${g.ctx.spec.id}: no room for the ${kind}`);
  return { id: -1, kind, x: (g.rect.x0 + g.rect.x1) / 2, z: (g.rect.z0 + g.rect.z1) / 2, yaw: 0 };
}

/** Scatter a few pieces of clutter (crates, barrels) around the compound; whatever fits, fits. */
export function clutter(g: GoalCtx, kind: PropKind, count: number, near?: Point, spread = 6): void {
  for (let n = 0; n < count; n++) {
    const i = g.inner;
    const within = near ? {
      x0: Math.max(i.x0, near.x - spread), z0: Math.max(i.z0, near.z - spread),
      x1: Math.min(i.x1, near.x + spread), z1: Math.min(i.z1, near.z + spread),
    } : i;
    structure(g, kind, { within, margin: 0.5 });
  }
}

/** Post a guard on the nearest free cell to a point. */
export function guard(
  g: GoalCtx, x: number, z: number, facing = 0, kind?: EnemyKind, extra: Partial<SpawnDef> = {},
): SpawnDef | null {
  const spot = g.map.nearestOpen(x, z, 5, NO_STAND | FLAG.keep);
  if (!spot) return null;
  return g.map.spawn({ kind: kind ?? (g.elite ? 'elite' : 'rifleman'), x: spot[0], z: spot[1], facing, ...extra });
}

/** A guard standing at the south face of a prop - its door, its flap, its gate. */
export function guardAtDoor(
  g: GoalCtx, prop: PropDef, kind?: EnemyKind, extra: Partial<SpawnDef> = {},
): SpawnDef | null {
  const b = g.map.bounds(prop.kind, prop.x, prop.z, prop);
  return guard(g, prop.x, b.z1 + 1.6, 0, kind, extra);
}

/** Some guards in the open inside the compound. */
export function guardsAbout(g: GoalCtx, count: number): void {
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 8; tries++) {
      const x = g.rng.range(g.inner.x0, g.inner.x1);
      const z = g.rng.range(g.inner.z0, g.inner.z1);
      const facing = g.rng.range(-0.8, 0.8);
      // Where the open end of the quiet way in is already fixed (the border), nobody is posted looking at it.
      if (overlooksQuietApproach(g.ctx, { kind: 'rifleman', x, z, facing })) continue;
      guard(g, x, z, facing);
      break;
    }
  }
}

/* ------------------------------------------------------------------ assert helpers */

export const targetsOf = (def: MissionDef, kind?: PropKind): PropDef[] =>
  def.props.filter((p) => def.objective.targetPropIds.includes(p.id) && (kind === undefined || p.kind === kind));

type RuleOf<T extends ObjectiveRule['type']> = Extract<ObjectiveRule, { type: T }>;

export function ruleOf<T extends ObjectiveRule['type']>(def: MissionDef, type: T): RuleOf<T> | undefined {
  return def.objective.rules.find((r): r is RuleOf<T> => r.type === type);
}

export const insideRect = (r: Rect, x: number, z: number): boolean => x >= r.x0 && x <= r.x1 && z >= r.z0 && z <= r.z1;

export const countKind = (def: MissionDef, kind: PropKind): number => def.props.filter((p) => p.kind === kind).length;

/**
 * Does a man hold a post close enough to a building of this kind that a knife there wins his uniform? The sim
 * wants the RANGER within `OBJECTIVES.interiorReach` of its wall when the man dies (objectives/disguise.ts).
 */
export function uniformDonor(def: MissionDef, kind: PropKind): boolean {
  // ROUND8: a dog wears no uniform.
  const men = def.spawns.filter((s) => s.dormantUntil === undefined && s.mans === undefined && s.patrol === undefined
    && s.kind !== 'dog');
  return def.props.filter((p) => p.kind === kind).some((p) => collidersFor(p).some((c) => men.some((s) => {
    const dx = Math.max(0, Math.abs(s.x - c.x) - c.hx);
    const dz = Math.max(0, Math.abs(s.z - c.z) - c.hz);
    return dx * dx + dz * dz <= OBJECTIVES.interiorReach * OBJECTIVES.interiorReach;
  })));
}

/** Collects failures: `check(ok, message)` records the message when the condition does not hold. */
export function checker(prefix: string): { check: (ok: boolean, message: string) => void; out: string[] } {
  const out: string[] = [];
  return { out, check: (ok, message) => { if (!ok) out.push(`${prefix}: ${message}`); } };
}
